import React, { useState } from "react";
import { useQueryClient } from "react-query";

import { getErrorMessage } from "services/api";

import { Button } from "components/Button";
import { Input } from "components/Input";
import { Radio } from "components/Radio";
import { Checkbox } from "components/Checkbox";
import { ItemSelect } from "components/ItemSelect";
import { TimePicker } from "components/TimePicker";
import { Icon } from "assets/icons";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace, Divider } from "components/Spacing";
import {
  useModal,
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "components/Modal";

import { createPromotion, updatePromotion } from "./api";
import styles from "./styles.module.scss";

const LABEL = 150;
const INPUT_W = 220;

const DAYS = [
  { field: "monday", label: "Пн" },
  { field: "tuesday", label: "Вт" },
  { field: "wednesday", label: "Ср" },
  { field: "thursday", label: "Чт" },
  { field: "friday", label: "Пт" },
  { field: "saturday", label: "Сб" },
  { field: "sunday", label: "Вс" },
];

const emptyInterval = () => ({ starts: "", ends: "", value: "" });

/** Модалка создания/редактирования акции */
export const PromotionModal = ({ promotion }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const isEdit = Boolean(promotion?.stockid);

  const [name, setName] = useState(promotion?.name ?? "");
  const [importcode, setImportcode] = useState(promotion?.importcode ?? "");
  const [starttime, setStarttime] = useState(promotion?.starttime ?? "");
  const [endtime, setEndtime] = useState(promotion?.endtime ?? "");
  // ispercent: 1 — процентная, 0 — ценовая
  const [ispercent, setIspercent] = useState(
    promotion?.ispercent != null ? Number(promotion.ispercent) : 0
  );
  const [days, setDays] = useState(() => {
    const init = {};
    for (const { field } of DAYS) {
      init[field] = promotion ? Number(promotion[field]) === 1 : false;
    }
    return init;
  });

  // товары: [{ itemid, name }]
  const [items, setItems] = useState(() => promotion?.items ?? []);
  // интервалы: [{ starts, ends, value }]
  const [intervals, setIntervals] = useState(() => {
    const list = (promotion?.intervals ?? []).map((i) => ({
      starts: String(i.starts ?? ""),
      ends: String(i.ends ?? ""),
      value: String(
        Number(promotion?.ispercent) === 1 ? (i.percent ?? "") : (i.price ?? "")
      ),
    }));
    return list.length ? list : [emptyInterval()];
  });

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  /* ---- товары ---- */

  const addItem = (option, item) => {
    if (!option?.value) return;
    const itemid = Number(option.value);
    setItems((prev) =>
      prev.some((i) => Number(i.itemid) === itemid)
        ? prev
        : [...prev, { itemid, name: item?.name || option.label }]
    );
  };

  const removeItem = (itemid) =>
    setItems((prev) => prev.filter((i) => Number(i.itemid) !== Number(itemid)));

  /* ---- интервалы ---- */

  const patchInterval = (index, update) =>
    setIntervals((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...update } : row))
    );

  const removeInterval = (index) =>
    setIntervals((prev) => prev.filter((_, i) => i !== index));

  /* ---- сохранение ---- */

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Укажите наименование");
      return;
    }
    const cleanIntervals = intervals
      .filter((i) => i.starts !== "" || i.ends !== "" || i.value !== "")
      .map((i) => ({
        starts: Number(i.starts) || 0,
        ends: Number(i.ends) || 0,
        value: Number(i.value) || 0,
      }));

    setSaving(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        importcode: importcode.trim() || null,
        starttime: starttime || null,
        endtime: endtime || null,
        ispercent,
        itemids: items.map((i) => Number(i.itemid)),
        intervals: cleanIntervals,
      };
      for (const { field } of DAYS) {
        payload[field] = days[field] ? 1 : 0;
      }
      if (isEdit) {
        await updatePromotion({ ...payload, stockid: promotion.stockid });
      } else {
        await createPromotion(payload);
      }
      queryClient.invalidateQueries(["promotions"]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell className={styles.modal}>
      <ModalHeader
        title={isEdit ? `Акция "${promotion.name}"` : "Новая акция"}
        onClose={closeModal}
      />
      <ModalBody>
        <div className={styles.columns}>
          <div className={styles.fieldsColumn}>
            {isEdit && (
              <>
                <Input
                  label="Код"
                  labelWidth={LABEL}
                  value={promotion.stockid}
                  disabled
                  inputWidth={100}
                />
                <VerticalSpace size={12} />
              </>
            )}
            <Input
              label="Наименование"
              labelWidth={LABEL}
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              inputWidth={INPUT_W}
            />
            <VerticalSpace size={12} />
            <Input
              label="Внешний код"
              labelWidth={LABEL}
              value={importcode}
              onChange={(ev) => setImportcode(ev.target.value)}
              inputWidth={INPUT_W}
            />
            <VerticalSpace size={16} />
            <div className={styles.inlineRow}>
              <MediumText size={13} style={{ width: LABEL, flexShrink: 0 }}>
                Время действия:
              </MediumText>
              <TimePicker
                label="с"
                value={starttime || null}
                onChange={(val) => setStarttime(val || "")}
              />
              <TimePicker
                label="по"
                value={endtime || null}
                onChange={(val) => setEndtime(val || "")}
              />
            </div>
            <VerticalSpace size={16} />
            <div className={styles.inlineRow}>
              <MediumText size={13} style={{ width: LABEL, flexShrink: 0 }}>
                Дни действия:
              </MediumText>
              <div className={styles.daysRow}>
                {DAYS.map(({ field, label }) => (
                  <Checkbox
                    key={field}
                    label={label}
                    checked={days[field]}
                    onChange={(checked) =>
                      setDays((prev) => ({ ...prev, [field]: checked }))
                    }
                  />
                ))}
              </div>
            </div>
            <VerticalSpace size={16} />
            <div className={styles.inlineRow}>
              <MediumText size={13} style={{ width: LABEL, flexShrink: 0 }}>
                Тип акции:
              </MediumText>
              <Radio
                id="promo-type-percent"
                name="promo-type"
                label="Процентная"
                checked={ispercent === 1}
                onChange={() => setIspercent(1)}
              />
              <Radio
                id="promo-type-price"
                name="promo-type"
                label="Ценовая"
                checked={ispercent === 0}
                onChange={() => setIspercent(0)}
              />
            </div>

            <VerticalSpace size={20} />
            <Divider />
            <VerticalSpace size={16} />

            <MediumText size={14}>Интервалы</MediumText>
            <VerticalSpace size={8} />
            <div className={styles.intervalsHeader}>
              <RegularText size={13} color="muted" className={styles.intervalCell}>
                От (кол-во)
              </RegularText>
              <RegularText size={13} color="muted" className={styles.intervalCell}>
                До (кол-во)
              </RegularText>
              <RegularText size={13} color="muted" className={styles.intervalCell}>
                {ispercent === 1 ? "Процент" : "Цена, ₽"}
              </RegularText>
            </div>
            {intervals.map((row, index) => (
              // Интервалы без стабильных id — индекс безопасен, порядок не меняется
              // eslint-disable-next-line react/no-array-index-key
              <div className={styles.intervalRow} key={index}>
                <Input
                  type="number"
                  value={row.starts}
                  onChange={(ev) => patchInterval(index, { starts: ev.target.value })}
                  inputWidth={120}
                />
                <Input
                  type="number"
                  value={row.ends}
                  onChange={(ev) => patchInterval(index, { ends: ev.target.value })}
                  inputWidth={120}
                />
                <Input
                  type="number"
                  value={row.value}
                  onChange={(ev) => patchInterval(index, { value: ev.target.value })}
                  inputWidth={120}
                />
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => removeInterval(index)}
                  title="Удалить интервал"
                >
                  <Icon type="close" size={12} />
                </button>
              </div>
            ))}
            <VerticalSpace size={8} />
            <Button
              icon="plus"
              onClick={() => setIntervals((prev) => [...prev, emptyInterval()])}
            >
              Добавить интервал
            </Button>
          </div>

          <div className={styles.itemsColumn}>
            <MediumText size={14}>Товары акции</MediumText>
            <VerticalSpace size={8} />
            <ItemSelect
              label=""
              value={null}
              onChange={addItem}
              width={320}
              placeholder="Добавить товар..."
              isClearable={false}
            />
            <VerticalSpace size={8} />
            <div className={styles.itemsList}>
              {items.length === 0 ? (
                <RegularText size={13} color="muted">
                  Товары не выбраны
                </RegularText>
              ) : (
                items.map((item) => (
                  <div className={styles.itemRow} key={item.itemid}>
                    <RegularText size={13} className={styles.itemName}>
                      {item.name}
                    </RegularText>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => removeItem(item.itemid)}
                      title="Убрать товар"
                    >
                      <Icon type="close" size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {!!error && (
          <RegularText size={13} color="danger" className={styles.footerError}>
            {error}
          </RegularText>
        )}
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          loading={saving}
        >
          Сохранить
        </Button>
        <Button variant="tertiary" onClick={closeModal}>
          Отмена
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
